// transfer.js — moving a stopped server onto another node: the calls, the vocabulary and the guards.
//
// The browser drives both halves with the one session. The SOURCE node mints a single-use ticket for a
// chosen target; the TARGET node checks it (the preflight) and then runs the transfer as a job the
// browser only watches. Neither node is asked on the other's behalf, so every call here names the node
// it goes to. The wire is `kgsm-docs/plans/instance-transfer.md`, "kgsm-api routes".
//
// No React and no store: the `check:transfer` script loads this module in Node. The calls take the
// host-scoped client they go through (`api.host`), so a check hands in a recording one.

import { api } from "./apiClient.js";
import { fmtBytes } from "./formatting.js";
import { originOf } from "./oidc.js";

// The refusal codes every route, the preflight and a failed job share, in the words a person reads. A
// code the panel has no sentence for is shown as the node gave it (`refusalWords`), never replaced by a
// guess.
const REFUSAL_WORDS = {
  instance_running: "The server is running. Stop it first.",
  instance_locked: "The server is locked by a transfer already under way.",
  transfer_in_progress: "This server is already being transferred.",
  ticket_invalid: "The transfer ticket has expired or was already used.",
  target_not_node: "The chosen member is not a node.",
  member_unreachable: "The other node can’t be reached.",
  engine_version_mismatch: "The two nodes run different KGSM versions.",
  requirement_missing: "The target node lacks something this game needs.",
  insufficient_disk: "The target node doesn’t have enough free disk.",
  port_conflict: "A port this server uses is taken on the target node.",
  integrity_failed: "The copied files didn’t match the originals.",
  start_failed: "The server didn’t start on the target node.",
  transfer_committed: "The transfer already completed.",
  transfer_not_locked: "The source server wasn’t locked for the copy.",
  transfer_interrupted: "The transfer was interrupted.",
  transfer_cancelled: "The transfer was cancelled.",
  // The engine's own refusals the preflight passes through. The ones a person can act on have words;
  // the others are shown as the node gave them.
  library_offline: "The chosen library isn’t mounted on the target node.",
  library_not_found: "The target node has no library by that name.",
  library_overlaps_registry: "The chosen library overlaps another one on the target node.",
  blueprint_missing: "The target node lacks the blueprint this server runs.",
};

// The job's phases, in the order they run. `done` and `failed` end it.
const TRANSFER_PHASES = ["preflight", "locking", "transferring", "importing", "committing", "done", "failed"];

const PHASE_WORDS = {
  preflight: "Checking",
  locking: "Locking the source",
  transferring: "Copying files",
  importing: "Setting up on the target",
  committing: "Finishing",
  done: "Transferred",
  failed: "Failed",
};

/// A refusal as a sentence. `detail` is the node's own elaboration and rides after the code's words,
/// because it is the half that names the figure or the requirement.
function refusalWords(refusal) {
  if (!refusal) return "";
  const words = REFUSAL_WORDS[refusal.code] || refusal.code || "Refused.";
  return refusal.detail ? words + " " + refusal.detail : words;
}

/// A rejected call as the refusal it carries: a node answers a refusal with the code in the error
/// envelope. Anything without a code is a transport or session failure and is `null` here, for the
/// caller to word as the failure it is.
function refusalOf(err) {
  const code = err && err.envCode;
  if (!code || !(code in REFUSAL_WORDS)) return null;
  return { code, detail: null };
}

/// What a server's `transfer` field says, as a sentence. `peerName` is the target node's name. The
/// field is served by the source only: `ticketed` is a ticket minted and not yet redeemed, `locked` is
/// the copy under way.
function transferWords(transfer, peerName) {
  if (!transfer) return "";
  const peer = peerName || "another node";
  return transfer.state === "ticketed" ? "Preparing a transfer to " + peer : "Transferring to " + peer;
}

/// Whether this server may be offered a transfer right now, and the reason when not. The person's
/// access is a different question (`serverCallRefusal`): this is the server's own state.
function transferBlock(server) {
  if (!server) return "Not available";
  if (server.transfer) return "A transfer is already under way";
  if (server.status === "online" || server.status === "starting") return "Stop the server before transferring it";
  if (server.status === "stopping" || server.status === "restarting") return "Waiting for the server to finish stopping";
  return null;
}

/// A job's transfer progress. `fraction` is null until a total is stated: bytes done over an unstated
/// total is not a proportion.
function transferProgress(job) {
  const t = job && job.transfer;
  if (!t) return null;
  const total = Number.isFinite(t.bytesTotal) && t.bytesTotal > 0 ? t.bytesTotal : null;
  const done = Number.isFinite(t.bytesDone) ? t.bytesDone : null;
  return {
    phase: t.phase,
    partId: t.partId ?? null,
    bytesDone: done,
    bytesTotal: total,
    targetInstanceId: t.targetInstanceId ?? null,
    fraction: total != null && done != null ? Math.min(1, done / total) : null,
  };
}

/// "1.2 GB of 4.0 GB", or whichever half is known.
function bytesWords(progress) {
  if (!progress) return null;
  const done = progress.bytesDone != null ? fmtBytes(progress.bytesDone) : null;
  const total = progress.bytesTotal != null ? fmtBytes(progress.bytesTotal) : null;
  if (done && total) return done + " of " + total;
  return done || total;
}

/// A node's cluster MEMBER id, which is what a transfer names a node by. The panel routes by host id, and
/// the two are separate settings, so the join is the address: the roster row (`members`, the node's
/// answer, and `self`, the node the roster was read from, which a roster never lists) whose `clientUrl` is
/// the origin the connection `hostId` is reached at. `null` when neither holds such a row; nothing falls
/// back to the host id.
function memberIdOf(hostId, members, connections, self) {
  const conn = (connections || []).find((c) => c.id === hostId);
  const origin = conn ? originOf(conn.url) : "";
  const row = origin ? (members || []).concat(self ? [self] : []).find((m) => m.nodeId && originOf(m.clientUrl) === origin) : null;
  return row ? row.nodeId : null;
}

/// The host id a member id is driven as: the connection reached at that member's address, or `null`.
function hostIdOfMember(memberId, members, connections, self) {
  const row = (members || []).concat(self ? [self] : []).find((m) => m.nodeId === memberId);
  const origin = row ? originOf(row.clientUrl) : "";
  const conn = origin ? (connections || []).find((c) => c.id && originOf(c.url) === origin) : null;
  return conn ? conn.id : null;
}

/// What a node can take in, from its host record: the free memory and the free disk in the library a
/// transfer would land in (the named one, else the roomiest online one). Each is `null` when nothing
/// measured it, never zero.
function nodeRoom(host, library) {
  const libs = (host && host.libraries) || [];
  const online = libs.filter((l) => l.online && l.free_bytes != null);
  const chosen = library ? libs.find((l) => l.name === library) : null;
  const freeDiskBytes = chosen
    ? (chosen.online && chosen.free_bytes != null ? chosen.free_bytes : null)
    : (online.length ? Math.max(...online.map((l) => l.free_bytes)) : null);
  const gb = host && host.ram && host.ram.total_gb ? host.ram.free_gb : null;
  return {
    freeDiskBytes,
    freeRamBytes: gb != null && Number.isFinite(gb) ? gb * 1024 * 1024 * 1024 : null,
  };
}

/// The four person-facing calls, over a host-scoped client factory (`api.host`).
function transferCalls(hostOf) {
  const serverPath = (id) => "/servers/" + encodeURIComponent(id) + "/transfers";
  return {
    // On the SOURCE: refuse a running or already-transferring server, store the record, mint the
    // ticket. Answers `{ transferId, ticket, sourceMemberId, expiresAt, summary }`.
    mint: (sourceHostId, serverId, targetMemberId) =>
      hostOf(sourceHostId).post(serverPath(serverId), { targetMemberId }),
    // On the SOURCE: drop the transfer a ticket was minted for. `force` is the operator's override of a
    // lock that outlived its transfer.
    cancel: (sourceHostId, serverId, force = false) =>
      hostOf(sourceHostId).del(serverPath(serverId) + "/current" + (force ? "?force=true" : "")),
    // On the TARGET: may it import here, and will it work. Takes no lock and spends no ticket.
    // Answers `{ ok, refusals: [{ code, detail }], summary }`.
    preflight: (targetHostId, ticket, library) =>
      hostOf(targetHostId).post("/transfers/preflight", requestOf(ticket, library)),
    // On the TARGET: start it. Answers `{ jobId, transferId }`; the rest is the job.
    start: (targetHostId, ticket, library) =>
      hostOf(targetHostId).post("/transfers", requestOf(ticket, library)),
  };
}

function requestOf(ticket, library) {
  const body = {
    sourceMemberId: ticket.sourceMemberId,
    transferId: ticket.transferId,
    ticket: ticket.ticket,
  };
  if (library) body.library = library;
  return body;
}

const transfers = transferCalls(api.host);

export {
  bytesWords, hostIdOfMember, memberIdOf, nodeRoom, PHASE_WORDS, refusalOf, refusalWords, REFUSAL_WORDS, transferBlock, transferCalls,
  TRANSFER_PHASES, transferProgress, transfers, transferWords,
};
